import type { Author, Embed, Post } from "../lib/model.ts";
import { quoteUri } from "../lib/model.ts";
import { richText } from "../lib/richtext.ts";
import { blobUrl, bskyUrl, postUri, webUrl } from "../lib/uri.ts";
import { viewImages } from "./image-viewer.tsx";

export function postCard(
  post: Post,
  authors: Map<string, Author>,
  quotes: Map<string, Post>,
  quoted = false,
): Element {
  const parsed = postUri(post.uri, true);
  const did = parsed.host;
  const author = authors.get(did);
  const record = post.record;
  const name = author?.displayName ||
    (author?.handle ? `@${author.handle}` : did);
  const article = (
    <article class={quoted ? "post quote" : "post"}>
      <div class="avatar">
        {author?.avatar
          ? (
            <img
              src={author.avatar}
              alt=""
              loading="lazy"
              referrerPolicy="no-referrer"
              _onerror={(event) => {
                (event.currentTarget as HTMLImageElement).hidden = true;
              }}
            />
          )
          : undefined}
      </div>
      <header class="post-header">
        <div class="identity">
          <a
            class="author-name"
            href={`https://bsky.app/profile/${did}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {name}
          </a>
          <span class="handle" title={did}>
            {author?.handle ? `@${author.handle}` : did}
          </span>
        </div>
      </header>
      {record
        ? (
          <div class="post-text">
            {richText(record.text, record.facets).map((part) =>
              part.href
                ? (
                  <a href={part.href} target="_blank" rel="noopener noreferrer">
                    {part.text}
                  </a>
                )
                : document.createTextNode(part.text)
            )}
          </div>
        )
        : <p class="unavailable">{post.error ?? "Post unavailable."}</p>}
    </article>
  );
  if (record?.labels?.values.length) {
    article.append(
      <p class="labels">
        {`Author labels: ${
          record.labels.values.map((label) => label.val).join(", ")
        }`}
      </p>,
    );
  }
  if (record?.embed) {
    const embed = record.embed;
    const media = embed.$type === "app.bsky.embed.recordWithMedia"
      ? embed.media
      : embed;
    if (media.$type !== "app.bsky.embed.record") {
      const content = renderMedia(media, author, did);
      if (content) {
        if (record.labels?.values.length) {
          article.append(
            <details>
              <summary>Show labelled media</summary>
              {content}
            </details>,
          );
        } else article.append(content);
      }
    }
    const uri = quoteUri(record);
    if (uri) {
      try {
        postUri(uri, true);
        const quote = quotes.get(uri);
        if (!quoted && quote) {
          article.append(postCard(quote, authors, quotes, true));
        } else {article.append(
            <a
              class="quote-link"
              href={`#/${uri}`}
            >
              Read quoted thread ↗
            </a>,
          );}
      } catch { /* Unknown quote references remain non-interactive. */ }
    }
  }
  const date = record && new Date(record.createdAt);
  const recordUrl = author?.pds
    ? new URL(
      `${author.pds.replace(/\/$/, "")}/xrpc/com.atproto.repo.getRecord`,
    )
    : undefined;
  if (recordUrl) {
    recordUrl.search = new URLSearchParams({
      repo: did,
      collection: parsed.collection,
      rkey: parsed.rkey,
    }).toString();
  }
  article.append(
    <footer class="post-footer">
      {date && Number.isFinite(date.getTime())
        ? (
          <a href={`#/${post.uri}`}>
            <time dateTime={date.toISOString()} title={date.toISOString()}>
              {date.toLocaleString(undefined, {
                dateStyle: "medium",
                timeStyle: "short",
              })}
            </time>
          </a>
        )
        : undefined}
      <a href={bskyUrl(post.uri)} target="_blank" rel="noopener noreferrer">
        bsky.app ↗
      </a>
      {recordUrl
        ? (
          <a href={recordUrl.href} target="_blank" rel="noopener noreferrer">
            record ↗
          </a>
        )
        : undefined}
    </footer>,
  );
  return article;
}

type Media = Exclude<
  Embed,
  { $type: "app.bsky.embed.record" | "app.bsky.embed.recordWithMedia" }
>;

function renderMedia(
  media: Media,
  author: Author | undefined,
  did: string,
): Element | undefined {
  const container = <div class="media" />;
  if (media.$type === "app.bsky.embed.images") {
    container.classList.add("images");
    const images = media.images.flatMap((item) => {
      const src = blobUrl(author?.pds, did, item.image.ref.$link);
      return src ? [{ src, alt: item.alt }] : [];
    });
    for (const [index, item] of images.entries()) {
      container.append(
        <figure>
          <button
            type="button"
            class="image-trigger"
            ariaHasPopup="dialog"
            ariaLabel={item.alt
              ? `View image: ${item.alt}`
              : `View image ${index + 1} of ${images.length}`}
            _onclick={() => viewImages(images, index)}
          >
            <img
              src={item.src}
              alt={item.alt}
              loading="lazy"
              referrerPolicy="no-referrer"
            />
          </button>
          {item.alt
            ? (
              <details class="alt">
                <summary>ALT</summary>
                <p>{item.alt}</p>
              </details>
            )
            : undefined}
        </figure>,
      );
    }
  } else if (media.$type === "app.bsky.embed.video") {
    const src = blobUrl(author?.pds, did, media.video.ref.$link);
    if (!src) return;
    container.append(
      <video
        controls
        preload="none"
        playsInline
        ariaLabel={media.alt ?? "Post video"}
      >
        <source src={src} type={media.video.mimeType} />
      </video>,
    );
    if (media.alt) {
      container.append(<p class="media-description">{media.alt}</p>);
    }
    container.append(
      <a
        class="media-description"
        href={src}
        target="_blank"
        rel="noopener noreferrer"
      >
        Open original video ↗
      </a>,
    );
  } else {
    const card = media.external;
    const href = webUrl(card.uri);
    if (!href) return;
    const thumbnail = card.thumb &&
      blobUrl(author?.pds, did, card.thumb.ref.$link);
    container.append(
      <a class="external" href={href} target="_blank" rel="noopener noreferrer">
        {thumbnail
          ? (
            <img
              src={thumbnail}
              loading="lazy"
              alt=""
              referrerPolicy="no-referrer"
            />
          )
          : undefined}
        <strong>{card.title}</strong>
        <p>{card.description}</p>
        <span>{new URL(href).hostname}</span>
      </a>,
    );
  }
  return container.childNodes.length ? container : undefined;
}
